/**
 * WorkflowHealthBar
 *
 * Horizontal stacked bar visualising the status split for a group of jobs.
 * Rendered above `StatusBreakdownChips` on every Reports drill-in so the user
 * can spot workflow health for a job type / referral / priority etc in one look.
 *
 * Reads the exact same data the chips do — no new API calls.
 *
 * Props:
 *   jobs         — array of job records already scoped to the selected category
 *   statusColors — settings.status_colors map { 'Booked': '#3b82f6', ... }
 *   label        — optional heading suffix e.g. 'for NSW'
 *   height       — pixel height of the stacked bar (default 22)
 */
export const WorkflowHealthBar = ({ jobs = [], statusColors = {}, label = '', height = 22 }) => {
  if (!Array.isArray(jobs) || jobs.length === 0) return null;

  // Aggregate by status
  const counts = {};
  jobs.forEach((j) => {
    const s = j.status || 'Unknown';
    counts[s] = (counts[s] || 0) + 1;
  });
  const total = jobs.length;
  const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);

  const fallbackPalette = ['#3b82f6', '#f59e0b', '#ef4444', '#10b981', '#6366f1', '#9ca3af', '#a855f7', '#ec4899'];
  const colorFor = (name, idx) => statusColors?.[name] || fallbackPalette[idx % fallbackPalette.length];

  return (
    <div
      data-testid="workflow-health-bar"
      style={{
        marginBottom: '1rem',
        padding: '12px 14px',
        background: '#ffffff',
        borderRadius: '8px',
        border: '1px solid #e2e8f0',
      }}
    >
      <div
        style={{
          fontSize: '0.8rem',
          fontWeight: 600,
          color: '#475569',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          marginBottom: '0.55rem',
        }}
      >
        Workflow health{label ? ` for ${label}` : ''}
      </div>

      {/* Stacked bar */}
      <div
        role="img"
        aria-label={entries.map(([n, c]) => `${n}: ${c} (${((c / total) * 100).toFixed(1)}%)`).join(', ')}
        style={{
          display: 'flex',
          height: `${height}px`,
          width: '100%',
          borderRadius: '6px',
          overflow: 'hidden',
          border: '1px solid #e5e7eb',
          background: '#f8fafc',
        }}
      >
        {entries.map(([name, count], idx) => {
          const pct = (count / total) * 100;
          const color = colorFor(name, idx);
          return (
            <div
              key={name}
              title={`${name}: ${count} (${pct.toFixed(1)}%)`}
              data-testid={`workflow-bar-seg-${name.toLowerCase().replace(/\s+/g, '-')}`}
              style={{
                width: `${pct}%`,
                background: color,
                color: '#ffffff',
                fontSize: '0.72rem',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                minWidth: pct > 0 ? '4px' : 0,
                cursor: 'default',
              }}
            >
              {/* Only show the count inline when the segment is wide enough to fit it */}
              {pct >= 6 ? count : ''}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.85rem',
          marginTop: '0.55rem',
          fontSize: '0.75rem',
          color: '#475569',
        }}
      >
        {entries.map(([name, count], idx) => {
          const pct = (count / total) * 100;
          const color = colorFor(name, idx);
          return (
            <span
              key={name}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
              data-testid={`workflow-bar-legend-${name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <span
                style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '2px',
                  display: 'inline-block',
                  background: color,
                }}
              />
              {name} ({count} · {pct.toFixed(0)}%)
            </span>
          );
        })}
      </div>
    </div>
  );
};

export default WorkflowHealthBar;
